"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.balanceOpeningDifference = balanceOpeningDifference;
exports.yearEndSourceId = yearEndSourceId;
exports.yearEndClosingLines = yearEndClosingLines;
exports.checkPeriodClose = checkPeriodClose;
exports.periodCloseChecks = periodCloseChecks;
exports.nextChildAccountCode = nextChildAccountCode;
const money_1 = require("../money");
const document_rules_1 = require("../posting/document-rules");
const journal_number_1 = require("../posting/journal-number");
/**
 * يُكمل قيد افتتاح غير متزن بسطر واحد على `accountCode` حسب الإعداد — دالة خالصة،
 * والحساب نفسه يختاره المتصل (حساب الفرق أو الأرباح المرحلة). قيد متزن، أو وضع
 * `reject`، يمر بلا تغيير فيحكم عليه المحرك بفحصه العادى.
 *
 * السطر المُضاف آخر السطور ويحمل وصفًا صريحًا، فيظهر فى كشف الحساب كفرق لا كرصيد.
 */
function balanceOpeningDifference(lines, mode, accountCode, currency) {
    const difference = money_1.Money.sum(lines.map((line) => line.debit))
        .sub(money_1.Money.sum(lines.map((line) => line.credit)))
        .normalize();
    if (mode === 'reject' || difference.isZero()) {
        return { lines: [...lines], difference, balancingLine: null };
    }
    const memo = 'فرق أرصدة افتتاحية';
    const balancingLine = difference.isPositive()
        ? (0, document_rules_1.creditLine)(accountCode, difference, currency, { memo })
        : (0, document_rules_1.debitLine)(accountCode, difference.abs(), currency, { memo });
    return { lines: [...lines, balancingLine], difference, balancingLine };
}
// ---------------------------------------------------------------------------
// إقفال السنة (ADR-0006: `closing.yearEnd = auto_entry`)
// ---------------------------------------------------------------------------
/** مصدر قيد إقفال السنة ثابت لكل سنة — والفهرس الفريد على المصدر المنشور يمنع إقفالين. */
function yearEndSourceId(fiscalYear) {
    return 'year-end-' + fiscalYear;
}
/**
 * قيد إقفال السنة: كل حساب نتيجة برصيد يُعكس رصيده فيصير صفرًا، والصافى إلى
 * `retainedEarningsCode`. فالميزانية لا يتغير مجموعها: «نتيجة لم تُقفل» تنتقل
 * سطرًا بسطر إلى حقوق الملكية.
 *
 * السطور مرتبة بالكود فالقيد نفسه يتكرر لنفس الحركة. حسابات بلا رصيد لا سطر لها،
 * وسنة بلا أى رصيد نتيجة تُرجع سطورًا فارغة — والمتصل يرفضها بدل قيد صفرى.
 */
function yearEndClosingLines(movements, retainedEarningsCode, currency) {
    const memo = 'إقفال حسابات النتيجة';
    const lines = [];
    let netIncome = money_1.Money.zero();
    for (const movement of [...movements].sort((a, b) => a.code.localeCompare(b.code))) {
        const balance = money_1.Money.of(movement.credit).sub(movement.debit).normalize();
        if (balance.isZero())
            continue;
        netIncome = netIncome.add(balance);
        // رصيد دائن (إيراد) يُقفل بمدين، ورصيد مدين (مصروف) يُقفل بدائن
        lines.push(balance.isPositive()
            ? (0, document_rules_1.debitLine)(movement.code, balance, currency, { memo })
            : (0, document_rules_1.creditLine)(movement.code, balance.abs(), currency, { memo }));
    }
    netIncome = netIncome.normalize();
    if (lines.length > 0 && !netIncome.isZero()) {
        const resultMemo = 'صافى نتيجة السنة';
        lines.push(netIncome.isPositive()
            ? (0, document_rules_1.creditLine)(retainedEarningsCode, netIncome, currency, { memo: resultMemo })
            : (0, document_rules_1.debitLine)(retainedEarningsCode, netIncome.abs(), currency, { memo: resultMemo }));
    }
    return { lines, netIncome };
}
/**
 * قواعد قيد الإقفال حين `sourceType = period_close` — تُضاف لفحوص المحرك:
 *  - تاريخه آخر يوم فى الفترة، فيقع بعد كل حركة السنة ولا تُحسب سطوره فى قائمة دخلها.
 *  - مصدره `year-end-<السنة>`، فلا إقفالان لنفس السنة.
 *  - حسابات النتيجة فيه تُقفل لا تُفتح، وحسابات الميزانية يقبلها لأن الصافى يذهب
 *    إلى حقوق الملكية — وأى حساب ميزانية يختاره المتصل لا المحرك.
 */
function checkPeriodClose(intent, ctx) {
    const issues = [];
    if (intent.postingDate !== ctx.fiscalPeriod.endDate) {
        issues.push({
            code: 'CLOSING_DATE_NOT_PERIOD_END',
            message: 'a closing entry is dated on the last day of its fiscal period (' +
                ctx.fiscalPeriod.endDate +
                ')',
            details: { expected: ctx.fiscalPeriod.endDate, actual: intent.postingDate },
        });
    }
    const expectedSource = yearEndSourceId((0, journal_number_1.fiscalYearOf)(ctx.fiscalPeriod.startDate));
    if (intent.sourceId !== expectedSource) {
        issues.push({
            code: 'CLOSING_SOURCE_MISMATCH',
            message: 'a closing entry uses source id ' + expectedSource,
            details: { expected: expectedSource, actual: intent.sourceId ?? '' },
        });
    }
    return issues;
}
/**
 * قائمة ما قبل الإقفال — دالة خالصة على ما حمّله المتصل. كل المشاكل تُجمع معًا،
 * وما وضعه `off` لا يُفحص أصلاً ولا يظهر.
 *
 * البنك «مُطابق حتى نهاية الفترة» إذا كان آخر كشف مُطابق بتاريخ نهاية الفترة أو بعدها.
 */
function periodCloseChecks(input) {
    const result = { blocking: [], warnings: [], checks: [] };
    const add = (item) => (item.mode === 'block' ? result.blocking : result.warnings).push(item);
    const bankMode = input.modes.bankReconciled;
    if (bankMode !== 'off') {
        const pending = input.banks.filter((bank) => bank.hasMovement &&
            (bank.lastReconciledDate === null || bank.lastReconciledDate < input.periodEndDate));
        for (const bank of pending) {
            add({
                code: 'BANK_NOT_RECONCILED',
                mode: bankMode,
                message: 'bank account ' + bank.nameAr + ' is not reconciled up to ' + input.periodEndDate,
                details: {
                    bankAccountId: bank.id,
                    nameAr: bank.nameAr,
                    accountCode: bank.accountCode,
                    lastReconciledDate: bank.lastReconciledDate ?? '',
                    periodEndDate: input.periodEndDate,
                },
            });
        }
        result.checks.push({
            code: 'BANK_NOT_RECONCILED',
            mode: bankMode,
            passed: pending.length === 0,
        });
    }
    const openingMode = input.modes.openingDifference;
    if (openingMode !== 'off') {
        const balance = input.openingDifferenceBalance === null
            ? money_1.Money.zero()
            : money_1.Money.of(input.openingDifferenceBalance).normalize();
        if (!balance.isZero()) {
            add({
                code: 'OPENING_DIFFERENCE_NOT_ZERO',
                mode: openingMode,
                message: 'opening difference account ' +
                    input.openingDifferenceAccountCode +
                    ' still carries a balance',
                details: {
                    accountCode: input.openingDifferenceAccountCode,
                    balance: balance.toString(),
                },
            });
        }
        result.checks.push({
            code: 'OPENING_DIFFERENCE_NOT_ZERO',
            mode: openingMode,
            passed: balance.isZero(),
        });
    }
    return result;
}
// ---------------------------------------------------------------------------
// حساب فرعى جديد تحت أب (خزينة أو بنك بلا حساب — ADR-0006)
// ---------------------------------------------------------------------------
/**
 * الكود التالى الحر تحت أب: أكبر كود ابن + ١ بنفس الطول، أو أول ابن (`<بادئة>001`).
 * الأكواد فى الدليل كلها بنفس الطول، والابن يشارك أباه البادئة حتى أول صفر لاحق
 * (`111000` → `111001`…`111999`). نفاد المدى `null` — والمتصل يرفض بدل أن يكسر الشجرة.
 */
function nextChildAccountCode(parentCode, siblingCodes) {
    const prefix = parentCode.replace(/0+$/, '');
    const width = parentCode.length - prefix.length;
    if (width === 0 || !/^\d+$/.test(parentCode))
        return null;
    const max = 10 ** width - 1;
    let highest = 0;
    for (const code of siblingCodes) {
        if (code.length !== parentCode.length || !code.startsWith(prefix))
            continue;
        const suffix = code.slice(prefix.length);
        if (!/^\d+$/.test(suffix))
            continue;
        highest = Math.max(highest, Number(suffix));
    }
    const next = highest + 1;
    if (next > max)
        return null;
    return prefix + String(next).padStart(width, '0');
}
//# sourceMappingURL=closing.js.map